import React, { useEffect, useState, useCallback, useMemo } from "react";
import { PageLoader } from "../../../Components/Loader/PageLoader";
import {
  Box, Button, Typography, TextField, Tabs, Tab,
  CircularProgress, Chip, Divider, InputAdornment,
  IconButton, Select, MenuItem, Paper, Dialog, Grid, Tooltip,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import HistoryIcon from "@mui/icons-material/History";
import { CiSearch } from "react-icons/ci";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const LARAVEL_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();

interface PortalProduct {
  id: number; productName: string; productImg?: string;
  productPrice?: string; wholesaleprice?: string; custom_price?: number | null;
  productConversionUnit?: string; productCategoryId?: number;
}
interface OrderItem { product_id: number; name: string; qty: number; unit?: string; price: number; subtotal: number; }
interface ShopOrder { id: number; shop_id: number; items: OrderItem[]; total: number; status: string; notes?: string; created_at: string; }
interface DeliveryInfo { date: string; day_name: string; route_name?: string; delivery_time?: string; cutoff: string; cutoff_label: string; }

const authFetch = async (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem("portal_token") || "";
  return fetch(url, { ...options, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) } });
};

const UNITS = ["lb", "kg", "unit", "g", "box", "bag", "bunch", "dozen"];

const getDefaultUnit = (p: PortalProduct): string => {
  try {
    const cu = p.productConversionUnit;
    if (cu) { const parsed = typeof cu === "string" ? JSON.parse(cu) : cu; if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]) return parsed[0]; }
  } catch {}
  return "lb";
};

const PortalOrderPage: React.FC = () => {
  const navigate = useNavigate();
  const { shopId } = useParams<{ shopId: string }>();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [myProducts, setMyProducts] = useState<PortalProduct[]>([]);
  const [allProducts, setAllProducts] = useState<PortalProduct[]>([]);
  const [categories, setCategories] = useState<Record<string, string>>({});
  const [orders, setOrders] = useState<ShopOrder[]>([]);
  const [nextDelivery, setNextDelivery] = useState<string | null>(null);
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>(null);

  const [productTab, setProductTab] = useState(0);
  const [productSearch, setProductSearch] = useState("");
  const [masterSearch, setMasterSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [addingProductId, setAddingProductId] = useState<number | null>(null);

  const [cart, setCart] = useState<Record<number, { qty: string; unit: string }>>({});
  const [orderNotes, setOrderNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [creditStatus, setCreditStatus] = useState<any>(null);
  const [orderSuccess, setOrderSuccess] = useState<ShopOrder | null>(null);
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [mobileCartOpen, setMobileCartOpen] = useState(false);

  useEffect(() => {
    const state = location.state as any;
    if (state?.editOrder) {
      const order = state.editOrder as ShopOrder;
      const m: Record<number, { qty: string; unit: string }> = {};
      order.items.forEach(it => { m[it.product_id] = { qty: String(it.qty), unit: it.unit || "lb" }; });
      setCart(m); setOrderNotes(order.notes || ""); setEditingOrderId(order.id);
    }
    if (state?.reorder) {
      const order = state.reorder as ShopOrder;
      const m: Record<number, { qty: string; unit: string }> = {};
      order.items.forEach(it => { m[it.product_id] = { qty: String(it.qty), unit: it.unit || "lb" }; });
      setCart(m); setOrderNotes(""); setEditingOrderId(null);
    }
    if (state?.suggestedCart) setCart(state.suggestedCart);
  }, [location.state]);

  const loadProducts = useCallback(async () => {
    const token = localStorage.getItem("portal_token");
    if (!token) { navigate("/restaurant-portal/login", { replace: true }); return; }
    try {
      const [pRes, oRes, profRes] = await Promise.all([authFetch(`${API_URL}/portal/products`), authFetch(`${API_URL}/portal/orders`), authFetch(`${API_URL}/portal/profile`)]);
      if (pRes.status === 401) { localStorage.removeItem("portal_token"); navigate("/restaurant-portal/login", { replace: true }); return; }
      const [pData, oData, profData] = await Promise.all([pRes.json(), oRes.json(), profRes.json()]);
      if (profData.success && profData.credit_status) setCreditStatus(profData.credit_status);
      if (pData.success) {
        setMyProducts(pData.my_products || []); setAllProducts(pData.all_products || []);
        if (pData.categories) setCategories(pData.categories);
      }
      if (oData.success) {
        setOrders(oData.data || []);
        if (oData.next_delivery) setNextDelivery(oData.next_delivery);
        if (oData.delivery_info) setDeliveryInfo(oData.delivery_info);
      }
    } catch {} finally { setLoading(false); }
  }, [navigate]);
  useEffect(() => { loadProducts(); }, [loadProducts]);

  const getPrice = (p: PortalProduct) => {
    if (p.custom_price != null) return Number(p.custom_price);
    if (p.wholesaleprice) return Number(p.wholesaleprice);
    if (p.productPrice) return Number(p.productPrice);
    return 0;
  };

  const handleAddProduct = async (productId: number) => {
    setAddingProductId(productId);
    try { const res = await authFetch(`${API_URL}/portal/products/add`, { method: "POST", body: JSON.stringify({ product_id: productId }) }); const data = await res.json(); if (data.success) await loadProducts(); } finally { setAddingProductId(null); }
  };

  const recentItems = useMemo(() => {
    const freq: Record<number, { count: number; qty: number; unit: string; name: string }> = {};
    orders.filter(o => o.status !== "cancelled").slice(0, 3).forEach(o => {
      o.items.forEach(it => {
        if (!freq[it.product_id]) freq[it.product_id] = { count: 0, qty: 0, unit: it.unit || "lb", name: it.name };
        freq[it.product_id].count++; freq[it.product_id].qty += it.qty;
      });
    });
    return Object.entries(freq).map(([id, d]) => ({ id: Number(id), name: d.name, avgQty: Math.round((d.qty / d.count) * 10) / 10, unit: d.unit })).sort((a, b) => b.avgQty - a.avgQty).slice(0, 6);
  }, [orders]);

  const categoryList = useMemo(() => Object.entries(categories).map(([id, name]) => ({ id: Number(id), name: String(name) })), [categories]);

  const cartItems = myProducts.filter(p => { const c = cart[p.id]; return c && parseFloat(c.qty || "0") > 0; });
  const cartTotal = cartItems.reduce((s, p) => { const c = cart[p.id]; return s + getPrice(p) * parseFloat(c?.qty || "0"); }, 0);
  const removeFromCart = (id: number) => setCart(prev => { const n = { ...prev }; delete n[id]; return n; });

  const setQty = (id: number, val: string, unit?: string) => {
    if (val === "" || /^\d*\.?\d*$/.test(val)) setCart(prev => ({ ...prev, [id]: { qty: val, unit: unit || prev[id]?.unit || "lb" } }));
  };
  const incQty = (p: PortalProduct) => {
    const c = cart[p.id]; const cur = parseFloat(c?.qty || "0");
    setCart(prev => ({ ...prev, [p.id]: { qty: String(cur + 1), unit: c?.unit || getDefaultUnit(p) } }));
  };
  const decQty = (p: PortalProduct) => {
    const c = cart[p.id]; const cur = parseFloat(c?.qty || "0");
    if (cur <= 0) return;
    const next = Math.max(0, cur - 1);
    if (next === 0) removeFromCart(p.id);
    else setCart(prev => ({ ...prev, [p.id]: { qty: String(next), unit: c?.unit || getDefaultUnit(p) } }));
  };

  const handleSubmit = async () => {
    if (cartItems.length === 0) return; setSubmitting(true);
    try {
      const items: OrderItem[] = cartItems.map(p => { const c = cart[p.id]; const qty = parseFloat(c?.qty || "0"); const price = getPrice(p); return { product_id: p.id, name: p.productName, qty, unit: c?.unit || "lb", price, subtotal: price * qty }; });
      const res = await authFetch(`${API_URL}/portal/orders`, { method: "POST", body: JSON.stringify({ items, total: cartTotal, notes: orderNotes }) });
      const data = await res.json(); if (!data.success) throw new Error(data.message || "Failed");
      setOrderSuccess(data.data); setCart({}); setOrderNotes("");
    } catch (e: any) { alert(e.message); } finally { setSubmitting(false); }
  };

  const handleUpdate = async () => {
    if (!editingOrderId || cartItems.length === 0) { alert("Add at least one item"); return; } setSavingEdit(true);
    try {
      const items: OrderItem[] = cartItems.map(p => { const c = cart[p.id]; const qty = parseFloat(c?.qty || "0"); const price = getPrice(p); return { product_id: p.id, name: p.productName, qty, unit: c?.unit || "lb", price, subtotal: price * qty }; });
      const res = await authFetch(`${API_URL}/portal/orders/${editingOrderId}`, { method: "PUT", headers: { Accept: "application/json" }, body: JSON.stringify({ items, total: cartTotal, notes: orderNotes }) });
      const data = await res.json(); if (!data.success) { alert(data.message || "Failed"); return; }
      navigate(`/restaurant-portal/${shopId}/dashboard`, { replace: true });
    } catch (e: any) { alert(e.message); } finally { setSavingEdit(false); }
  };

  // Filtered products
  const filteredProducts = useMemo(() => {
    let list = myProducts;
    if (productSearch) list = list.filter(p => p.productName.toLowerCase().includes(productSearch.toLowerCase()));
    if (selectedCategory !== null) list = list.filter(p => p.productCategoryId === selectedCategory);
    return list;
  }, [myProducts, productSearch, selectedCategory]);

  const filteredAll = allProducts.filter(p => p.productName.toLowerCase().includes(masterSearch.toLowerCase()));
  const assignedIds = useMemo(() => new Set(myProducts.map(p => p.id)), [myProducts]);
  const isCutoffPassed = deliveryInfo?.cutoff ? dayjs().isAfter(dayjs(deliveryInfo.cutoff)) : false;

  if (loading) return <PageLoader height="40vh" />;

  // ── PRODUCT ROW COMPONENT ──
  const ProductRow = ({ p }: { p: PortalProduct }) => {
    const c = cart[p.id]; const qty = c ? parseFloat(c.qty || "0") : 0; const price = getPrice(p);
    const inCart = qty > 0;
    return (
      <Box sx={{
        display: "flex", alignItems: "center", gap: { xs: 1, md: 1.5 }, px: 2, py: 1.25,
        bgcolor: inCart ? "#F1F8F6" : "#fff",
        borderBottom: "1px solid #f0f0f0",
        transition: "background 0.12s",
        "&:hover": { bgcolor: inCart ? "#E8F5F1" : "#FAFAFA" },
      }}>
        {/* Name */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1.25 }}>
          {inCart && <CheckCircleIcon sx={{ fontSize: 16, color: "#009688", flexShrink: 0 }} />}
          <Box sx={{ minWidth: 0 }}>
            <Typography fontSize={13} fontWeight={600} color="#333" noWrap>{p.productName}</Typography>
            <Typography fontSize={11} color="#999">{getDefaultUnit(p)}</Typography>
          </Box>
        </Box>

        {/* Qty controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <IconButton size="small" onClick={() => decQty(p)} disabled={qty <= 0}
            sx={{ width: 28, height: 28, bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" }, "&.Mui-disabled": { bgcolor: "#fafafa" } }}>
            <RemoveIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <TextField size="small" value={c?.qty || ""} placeholder="0"
            onChange={(e) => setQty(p.id, e.target.value, c?.unit || getDefaultUnit(p))}
            inputProps={{ style: { fontSize: 13, padding: "4px 6px", textAlign: "center", width: 44 }, inputMode: "decimal" }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }} />
          <IconButton size="small" onClick={() => incQty(p)}
            sx={{ width: 28, height: 28, bgcolor: "#E0F2F1", color: "#009688", "&:hover": { bgcolor: "#B2DFDB" } }}>
            <AddIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>

        {/* Unit */}
        <Select size="small" value={c?.unit || getDefaultUnit(p)}
          onChange={(e) => setCart(prev => ({ ...prev, [p.id]: { qty: prev[p.id]?.qty || "", unit: e.target.value } }))}
          sx={{ fontSize: 11, minWidth: 58, flexShrink: 0, "& .MuiSelect-select": { py: 0.4, px: 0.75, fontSize: 11 }, borderRadius: 1.5, height: 28 }}>
          {UNITS.map(u => <MenuItem key={u} value={u} sx={{ fontSize: 11 }}>{u}</MenuItem>)}
        </Select>

        {/* Status indicator */}
        {inCart && (
          <Chip label="Added" size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: "#E0F2F1", color: "#009688", flexShrink: 0 }} />
        )}
      </Box>
    );
  };

  // ── CART PANEL ──
  const cartPanel = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f0f0f0", bgcolor: cartItems.length > 0 ? (editingOrderId ? "#ef6c00" : "#009688") : "#FAFAFA", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ShoppingCartIcon sx={{ fontSize: 18, color: cartItems.length > 0 ? "#fff" : "#bbb" }} />
          <Typography fontWeight={700} fontSize={14} color={cartItems.length > 0 ? "#fff" : "#555"}>
            {editingOrderId ? `Edit #${editingOrderId}` : "Your Order"}
          </Typography>
        </Box>
        {cartItems.length > 0 && <Chip label={cartItems.length} size="small" sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "#fff", fontWeight: 700, fontSize: 10, height: 20 }} />}
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", px: 2, py: 1.5 }}>
        {cartItems.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <ShoppingCartIcon sx={{ fontSize: 40, color: "#e8e8e8", mb: 1 }} />
            <Typography fontSize={13} color="#bbb" mb={0.5}>Your cart is empty</Typography>
            <Typography fontSize={11} color="#ddd">Add products from the list</Typography>
          </Box>
        ) : (
          cartItems.map(p => {
            const c = cart[p.id]; const qty = parseFloat(c?.qty || "0");
            return (
              <Box key={p.id} sx={{ py: 1, borderBottom: "1px solid #f8f8f8" }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontSize={12} fontWeight={600} color="#333" noWrap>{p.productName}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => removeFromCart(p.id)} sx={{ color: "#ddd", "&:hover": { color: "#d32f2f" }, mt: -0.25 }}>
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton size="small" onClick={() => decQty(p)} sx={{ width: 24, height: 24, bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }}><RemoveIcon sx={{ fontSize: 14 }} /></IconButton>
                    <TextField size="small" value={c?.qty || ""} onChange={(e) => setQty(p.id, e.target.value)}
                      inputProps={{ style: { fontSize: 12, padding: "3px 6px", textAlign: "center", width: 40 } }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }} />
                    <IconButton size="small" onClick={() => incQty(p)} sx={{ width: 24, height: 24, bgcolor: "#E0F2F1", "&:hover": { bgcolor: "#B2DFDB" } }}><AddIcon sx={{ fontSize: 14, color: "#009688" }} /></IconButton>
                    <Select size="small" value={c?.unit || getDefaultUnit(p)} onChange={(e) => setCart(prev => ({ ...prev, [p.id]: { qty: prev[p.id]?.qty || "0", unit: e.target.value } }))}
                      sx={{ fontSize: 10, ml: 0.5, "& .MuiSelect-select": { py: 0.25, px: 0.75, fontSize: 10 }, borderRadius: 1, minWidth: 45, height: 24 }}>
                      {UNITS.map(u => <MenuItem key={u} value={u} sx={{ fontSize: 11 }}>{u}</MenuItem>)}
                    </Select>
                  </Box>
                  <Typography fontSize={12} fontWeight={600} color="#999">{qty} {c?.unit}</Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {cartItems.length > 0 && (
        <Box sx={{ borderTop: "1px solid #e8e8e8", px: 2, py: 2, bgcolor: "#fff" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
            <Typography fontSize={14} fontWeight={700} color="#1a1a1a">{cartItems.length} item{cartItems.length !== 1 ? "s" : ""} selected</Typography>
          </Box>
          {deliveryInfo && (
            <Box sx={{ p: 1.25, mb: 1.5, borderRadius: 2, bgcolor: "#F1F8E9", border: "1px solid #DCEDC8" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                <LocalShippingIcon sx={{ fontSize: 12, color: "#558B2F" }} />
                <Typography fontSize={11} fontWeight={600} color="#558B2F">{deliveryInfo.day_name}, {dayjs(deliveryInfo.date).format("MMM D")}</Typography>
              </Box>
              {deliveryInfo.route_name && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AltRouteIcon sx={{ fontSize: 11, color: "#7CB342" }} />
                  <Typography fontSize={10} color="#7CB342">{deliveryInfo.route_name}</Typography>
                </Box>
              )}
              {!isCutoffPassed && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                  <AccessTimeIcon sx={{ fontSize: 11, color: "#F57F17" }} />
                  <Typography fontSize={10} color="#F57F17" fontWeight={600}>Cutoff: {deliveryInfo.cutoff_label}</Typography>
                </Box>
              )}
            </Box>
          )}
          <TextField size="small" fullWidth multiline rows={2} placeholder="Order notes (optional)..."
            value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 12 } }} />
          {/* Credit warning */}
          {creditStatus?.is_blocked && (
            <Box sx={{ p: 1.25, mb: 1.5, borderRadius: 2, bgcolor: "#FFEBEE", border: "1px solid #FFCDD2" }}>
              <Typography fontSize={11} fontWeight={600} color="#C62828">
                {creditStatus.has_overdue ? "Ordering blocked — overdue invoices" : "Credit limit exceeded"}
              </Typography>
            </Box>
          )}

          {editingOrderId ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button fullWidth variant="contained" disabled={savingEdit} onClick={handleUpdate} startIcon={<SendIcon sx={{ fontSize: 15 }} />}
                sx={{ borderRadius: 2, py: 1.25, fontWeight: 700, fontSize: 13, bgcolor: "#ef6c00", "&:hover": { bgcolor: "#e65100" }, textTransform: "none" }}>
                {savingEdit ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Update Order"}
              </Button>
              <Button fullWidth variant="text" size="small" onClick={() => { setEditingOrderId(null); setCart({}); setOrderNotes(""); }}
                sx={{ fontSize: 12, color: "#999", textTransform: "none" }}>Cancel</Button>
            </Box>
          ) : (
            <Button fullWidth variant="contained" disabled={submitting || creditStatus?.is_blocked} onClick={handleSubmit} startIcon={<SendIcon sx={{ fontSize: 15 }} />}
              sx={{ borderRadius: 2, py: 1.25, fontWeight: 700, fontSize: 14, bgcolor: creditStatus?.is_blocked ? "#999" : "#009688", "&:hover": { bgcolor: creditStatus?.is_blocked ? "#999" : "#00796B" }, textTransform: "none", boxShadow: creditStatus?.is_blocked ? "none" : "0 2px 8px rgba(0,150,136,0.3)" }}>
              {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : creditStatus?.is_blocked ? "Ordering Blocked" : "Submit Order"}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ position: "fixed", inset: 0, bgcolor: "#f5f6f8", zIndex: 1200, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ── HEADER ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e8e8e8", px: { xs: 2, md: 3 }, py: 1, display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
        <IconButton onClick={() => navigate(`/restaurant-portal/${shopId}/dashboard`)} sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }} size="small">
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography fontWeight={700} fontSize={16} color="#1a1a1a">
            {editingOrderId ? `Edit Order #${editingOrderId}` : "New Order"}
          </Typography>
          {deliveryInfo && (
            <Typography fontSize={11} color="#999">
              <LocalShippingIcon sx={{ fontSize: 11, mr: 0.3, verticalAlign: "middle" }} />
              {deliveryInfo.day_name}, {dayjs(deliveryInfo.date).format("MMM D")}
              {deliveryInfo.route_name && <> &middot; {deliveryInfo.route_name}</>}
            </Typography>
          )}
        </Box>
        <Button variant="contained" size="small" onClick={() => setMobileCartOpen(true)}
          startIcon={<ShoppingCartIcon sx={{ fontSize: 15 }} />}
          sx={{ display: { xs: "flex", md: "none" }, borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 12, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" } }}>
          {cartItems.length > 0 ? `${cartItems.length} items` : "Cart"}
        </Button>
        {cartItems.length > 0 && (
          <Chip icon={<ShoppingCartIcon sx={{ fontSize: 14 }} />}
            label={`${cartItems.length} item${cartItems.length !== 1 ? "s" : ""}`}
            sx={{ bgcolor: "#009688", color: "#fff", fontWeight: 700, fontSize: 12, height: 32, "& .MuiChip-icon": { color: "#fff" }, display: { xs: "none", md: "flex" } }} />
        )}
      </Box>

      {/* ── MAIN CONTENT ── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* ── LEFT: Products ── */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* Sticky: Search + Tabs + Categories */}
          <Box sx={{ flexShrink: 0, bgcolor: "#f5f6f8", px: { xs: 2, md: 3 }, pt: 2, pb: 1, borderBottom: "1px solid #e8e8e8" }}>
            {/* Search */}
            <TextField size="small" fullWidth
              placeholder={productTab === 0 ? "Search assigned products..." : "Search all products..."}
              value={productTab === 0 ? productSearch : masterSearch}
              onChange={(e) => { productTab === 0 ? setProductSearch(e.target.value) : setMasterSearch(e.target.value); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><CiSearch style={{ fontSize: 18, color: "#bbb" }} /></InputAdornment> }}
              sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 2.5, bgcolor: "#fff", fontSize: 13 } }} />

            {/* Toggle + Categories */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {/* Tab Toggle */}
              <Box sx={{ display: "inline-flex", bgcolor: "#fff", borderRadius: 2, border: "1px solid #e8e8e8", p: 0.35, flexShrink: 0 }}>
                {[
                  { label: "Assigned", count: myProducts.length },
                  { label: "Browse All", count: allProducts.length + myProducts.length },
                ].map((t, i) => (
                  <Button key={i} size="small" onClick={() => { setProductTab(i); setSelectedCategory(null); }}
                    sx={{ borderRadius: 1.5, textTransform: "none", fontSize: 11, fontWeight: 600, px: 1.5, py: 0.3, minWidth: 0,
                      ...(productTab === i
                        ? { bgcolor: "#009688", color: "#fff", "&:hover": { bgcolor: "#00796B" } }
                        : { bgcolor: "transparent", color: "#888", "&:hover": { bgcolor: "#f5f5f5" } }),
                    }}>
                    {t.label}
                    <Chip label={t.count} size="small" sx={{ ml: 0.5, height: 16, fontSize: 9, fontWeight: 700, bgcolor: productTab === i ? "rgba(255,255,255,0.25)" : "#f0f0f0", color: productTab === i ? "#fff" : "#999", "& .MuiChip-label": { px: 0.5 } }} />
                  </Button>
                ))}
              </Box>

              {/* Category Pills (only for assigned products tab) */}
              {productTab === 0 && categoryList.length > 0 && (
                <Box sx={{ display: "flex", gap: 0.75, overflow: "auto", flex: 1, minWidth: 0, "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}>
                  <Chip label="All" size="small" onClick={() => setSelectedCategory(null)}
                    sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 26, flexShrink: 0,
                      ...(selectedCategory === null ? { bgcolor: "#009688", color: "#fff" } : { bgcolor: "#fff", color: "#666", border: "1px solid #e0e0e0", "&:hover": { bgcolor: "#E0F2F1" } }),
                    }} />
                  {categoryList.map(cat => (
                    <Chip key={cat.id} label={cat.name} size="small" onClick={() => setSelectedCategory(cat.id === selectedCategory ? null : cat.id)}
                      sx={{ fontSize: 11, fontWeight: 600, cursor: "pointer", borderRadius: "8px", height: 26, flexShrink: 0,
                        ...(selectedCategory === cat.id ? { bgcolor: "#009688", color: "#fff" } : { bgcolor: "#fff", color: "#666", border: "1px solid #e0e0e0", "&:hover": { bgcolor: "#E0F2F1" } }),
                      }} />
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Scrollable Product List */}
          <Box sx={{ flex: 1, overflow: "auto" }}>

            {/* ── Recently Ordered ── */}
            {productTab === 0 && recentItems.length > 0 && !productSearch && selectedCategory === null && (
              <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderBottom: "1px solid #f0f0f0", bgcolor: "#FAFAFA" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <HistoryIcon sx={{ fontSize: 13, color: "#999" }} />
                  <Typography fontSize={11} fontWeight={600} color="#999" textTransform="uppercase" letterSpacing={0.3}>Quick Add — Recently Ordered</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                  {recentItems.map(ri => {
                    const inCart = cart[ri.id] && parseFloat(cart[ri.id].qty || "0") > 0;
                    return (
                      <Chip key={ri.id} label={`${ri.name} (${ri.avgQty} ${ri.unit})`}
                        onClick={() => { if (!inCart) setCart(prev => ({ ...prev, [ri.id]: { qty: String(ri.avgQty), unit: ri.unit } })); }}
                        icon={inCart ? <CheckCircleIcon sx={{ fontSize: 13 }} /> : <AddIcon sx={{ fontSize: 13 }} />}
                        variant={inCart ? "filled" : "outlined"} size="small"
                        sx={{ fontSize: 11, fontWeight: 500, cursor: "pointer", borderRadius: "8px", height: 26,
                          ...(inCart ? { bgcolor: "#E0F2F1", color: "#009688", "& .MuiChip-icon": { color: "#009688" } } : { borderColor: "#ddd", "&:hover": { borderColor: "#009688", bgcolor: "#E0F2F1" } }),
                        }} />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* ── Column Headers ── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 1.5 }, px: 2, py: 0.75, bgcolor: "#FAFAFA", borderBottom: "1px solid #e8e8e8", position: "sticky", top: 0, zIndex: 5 }}>
              <Typography fontSize={10} fontWeight={700} color="#bbb" textTransform="uppercase" letterSpacing={0.5} sx={{ flex: 1 }}>Product</Typography>
              <Typography fontSize={10} fontWeight={700} color="#bbb" textTransform="uppercase" letterSpacing={0.5} sx={{ width: 120, textAlign: "center", flexShrink: 0 }}>Quantity</Typography>
              <Typography fontSize={10} fontWeight={700} color="#bbb" textTransform="uppercase" letterSpacing={0.5} sx={{ width: 58, textAlign: "center", flexShrink: 0 }}>Unit</Typography>
              <Typography fontSize={10} fontWeight={700} color="#bbb" textTransform="uppercase" letterSpacing={0.5} sx={{ width: 55, textAlign: "center", flexShrink: 0 }}></Typography>
            </Box>

            {/* ── Assigned Products (list rows) ── */}
            {productTab === 0 && (
              <Box>
                {filteredProducts.map(p => <ProductRow key={p.id} p={p} />)}
                {filteredProducts.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <Typography fontSize={14} color="#bbb">{productSearch || selectedCategory !== null ? "No products match your filter" : "No assigned products"}</Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* ── Browse All ── */}
            {productTab === 1 && (
              <Box>
                {/* Assigned products that match search */}
                {myProducts.filter(p => p.productName.toLowerCase().includes(masterSearch.toLowerCase())).length > 0 && (
                  <>
                    <Box sx={{ px: 2, py: 0.75, bgcolor: "#F1F8F6", borderBottom: "1px solid #C8E6C9", display: "flex", alignItems: "center", gap: 0.75 }}>
                      <CheckCircleIcon sx={{ fontSize: 13, color: "#009688" }} />
                      <Typography fontSize={11} fontWeight={600} color="#009688">Your Assigned Products</Typography>
                    </Box>
                    {myProducts.filter(p => p.productName.toLowerCase().includes(masterSearch.toLowerCase())).map(p => <ProductRow key={p.id} p={p} />)}
                  </>
                )}

                {/* Unassigned products */}
                {filteredAll.length > 0 && (
                  <>
                    <Box sx={{ px: 2, py: 0.75, bgcolor: "#FAFAFA", borderBottom: "1px solid #e8e8e8", display: "flex", alignItems: "center", gap: 0.75 }}>
                      <StorefrontIcon sx={{ fontSize: 13, color: "#999" }} />
                      <Typography fontSize={11} fontWeight={600} color="#999">Other Products — add to your list first</Typography>
                    </Box>
                    {filteredAll.map(p => (
                      <Box key={p.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.25, borderBottom: "1px solid #f0f0f0", bgcolor: "#FAFAFA", "&:hover": { bgcolor: "#f5f5f5" } }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontSize={13} fontWeight={500} color="#777" noWrap>{p.productName}</Typography>
                          <Typography fontSize={11} color="#bbb">${getPrice(p).toFixed(2)} / {getDefaultUnit(p)}</Typography>
                        </Box>
                        <Button size="small" variant="outlined" disabled={addingProductId === p.id} onClick={() => handleAddProduct(p.id)}
                          startIcon={addingProductId === p.id ? <CircularProgress size={12} /> : <AddIcon sx={{ fontSize: 13 }} />}
                          sx={{ borderRadius: 2, fontSize: 11, fontWeight: 600, textTransform: "none", borderColor: "#ccc", color: "#009688", minWidth: 0, px: 1.5, "&:hover": { borderColor: "#009688", bgcolor: "#E0F2F1" } }}>
                          Add
                        </Button>
                      </Box>
                    ))}
                  </>
                )}

                {myProducts.filter(p => p.productName.toLowerCase().includes(masterSearch.toLowerCase())).length === 0 && filteredAll.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 8 }}><Typography fontSize={14} color="#bbb">No products found</Typography></Box>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* ── RIGHT: Cart Sidebar ── */}
        <Box sx={{ width: 340, flexShrink: 0, borderLeft: "1px solid #e8e8e8", bgcolor: "#fff", display: { xs: "none", md: "flex" }, flexDirection: "column" }}>
          {cartPanel}
        </Box>
      </Box>

      {/* Mobile Cart */}
      <Dialog open={mobileCartOpen} onClose={() => setMobileCartOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3, maxHeight: "85vh" } }}>
        {cartPanel}
      </Dialog>

      {/* Success */}
      <Dialog open={!!orderSuccess} onClose={() => { setOrderSuccess(null); navigate(`/restaurant-portal/${shopId}/dashboard`, { replace: true }); }}
        maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {orderSuccess && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CheckCircleIcon sx={{ fontSize: 56, color: "#2E7D32", mb: 2 }} />
            <Typography fontSize={20} fontWeight={700} color="#1B5E20" mb={0.5}>Order Submitted!</Typography>
            <Typography fontSize={13} color="text.secondary" mb={2.5}>Order #{orderSuccess.id} placed successfully.</Typography>
            {deliveryInfo && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2.5, p: 1.5, bgcolor: "#E8F5E9", borderRadius: 2 }}>
                <LocalShippingIcon sx={{ color: "#2E7D32", fontSize: 18 }} />
                <Typography fontSize={13} fontWeight={600} color="#2E7D32">Expected: {deliveryInfo.day_name}, {dayjs(deliveryInfo.date).format("MMM D")}</Typography>
              </Box>
            )}
            <Button fullWidth variant="contained" onClick={() => { setOrderSuccess(null); navigate(`/restaurant-portal/${shopId}/dashboard`, { replace: true }); }}
              sx={{ borderRadius: 2, py: 1.25, fontWeight: 700, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" }, textTransform: "none" }}>
              Back to Dashboard
            </Button>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default PortalOrderPage;
